import React, { useEffect, useRef } from 'react';
import { components as selectComponents } from 'react-select';
import { Tooltip } from 'primereact/tooltip';

const createTooltipTargetClass = (prefix = 'async-select-overflow-badge') => `${prefix}-${Math.random().toString(36).slice(2, 11)}`;
const forceChipClass = 'async-select-summarized-chip-force';

const assignRef = (ref, node) => {
  if (typeof ref === 'function') {
    ref(node);
    return;
  }
  if (ref && typeof ref === 'object') {
    ref.current = node;
  }
};

/** Resets horizontal scroll when the menu closes so the first chip stays visible. */
function SummarizedValueContainer(props) {
  const { innerRef, selectProps, ...rest } = props;
  const containerRef = useRef(null);

  useEffect(() => {
    if (!selectProps.menuIsOpen && containerRef.current) {
      containerRef.current.scrollLeft = 0;
    }
  }, [selectProps.menuIsOpen]);

  const setContainerRef = (node) => {
    containerRef.current = node;
    assignRef(innerRef, node);
  };

  return <selectComponents.ValueContainer {...rest} selectProps={selectProps} innerRef={setContainerRef} />;
}

SummarizedValueContainer.displayName = 'SummarizedValueContainer';

const getOptionText = (option, selectProps) => {
  if (!option || typeof option !== 'object') return '';
  if (typeof selectProps?.getOptionLabel === 'function') {
    return selectProps.getOptionLabel(option) || '';
  }
  return option.label || option.lead_stage_name || option.practice_area_name || option.case_name || String(option.value ?? '');
};

/**
 * Factory for a react-select MultiValue that shows the first selection and a +N badge for the rest.
 *
 * @param {number} [maxVisible=1] - How many selected chips to show before collapsing to +N
 * @returns {React.ComponentType} MultiValue component for AsyncPaginate / react-select
 */
export function createSummarizedMultiValue(maxVisible = 1) {
  function SummarizedMultiValue(props) {
    const { index, getValue, selectProps } = props;
    const selected = getValue();
    const overflowCount = selected.length - maxVisible;
    const overflowTooltipClass = useRef(createTooltipTargetClass()).current;
    const chipTooltipClass = useRef(createTooltipTargetClass('async-select-first-chip')).current;

    if (index < maxVisible) {
      if (index === 0) {
        const label = getOptionText(props.data, selectProps);
        const chipClassName = [props.innerProps?.className, chipTooltipClass, forceChipClass].filter(Boolean).join(' ');

        return (
          <>
            <style>{`.${forceChipClass} .async-select-custom__multi-value__label{padding:0px !important;}`}</style>
            <selectComponents.MultiValue
              {...props}
              innerProps={{
                ...props.innerProps,
                className: chipClassName,
              }}
            />
            {label ? <Tooltip target={`.${chipTooltipClass}`} content={label} position="top" /> : null}
          </>
        );
      }

      const chipClassName = [props.innerProps?.className, forceChipClass].filter(Boolean).join(' ');
      return (
        <>
          <style>{`.${forceChipClass} .async-select-custom__multi-value__label{padding:0px !important;}`}</style>
          <selectComponents.MultiValue
            {...props}
            innerProps={{
              ...props.innerProps,
              className: chipClassName,
            }}
          />
        </>
      );
    }

    if (index === maxVisible && overflowCount > 0) {
      const hiddenLabels = selected
        .slice(maxVisible)
        .map((option) => getOptionText(option, selectProps))
        .filter(Boolean)
        .join(', ');

      return (
        <>
          <div className={`async-select-custom__overflow-badge ${overflowTooltipClass}`} aria-label={`${overflowCount} more selected`}>
            +{overflowCount}
          </div>
          <Tooltip target={`.${overflowTooltipClass}`} content={hiddenLabels} position="top" />
        </>
      );
    }

    return null;
  }

  SummarizedMultiValue.displayName = `SummarizedMultiValue(${maxVisible})`;
  return SummarizedMultiValue;
}

/** Shared react-select styles for summarized multi-select controls (single row, no chip scroll). */
export const getSummarizedMultiSelectStyles = (extraStyles = {}) => ({
  control: (base) => ({
    ...base,
    minHeight: '38px',
    maxHeight: '38px',
    flexWrap: 'nowrap',
    overflow: 'hidden',
  }),
  valueContainer: (base) => ({
    ...base,
    flexWrap: 'nowrap',
    overflow: 'hidden',
    alignItems: 'center',
  }),
  input: (base, state) => ({
    ...base,
    margin: 0,
    padding: '0px',
    ...(state.selectProps.menuIsOpen
      ? {
          flex: '0 1 72px',
          maxWidth: '88px',
          minWidth: '28px',
          width: 'auto',
        }
      : {
          width: 0,
          minWidth: 0,
          opacity: 0,
          position: 'absolute',
          pointerEvents: 'none',
        }),
  }),
  inputContainer: (base, state) => ({
    ...base,
    ...(state.selectProps.menuIsOpen
      ? {
          flex: '0 1 72px',
          maxWidth: '88px',
          minWidth: '28px',
        }
      : {
          flex: '0 0 0',
          width: 0,
          margin: 0,
          padding: 0,
        }),
  }),
  multiValue: (base, state) => ({
    ...base,
    backgroundColor: 'var(--black-100)',
    border: 0,
    borderRadius: '8px',
    color: 'var(--black-700)',
    margin: 0,
    padding: '0.25rem 0.5rem',
    justifyContent: 'space-between',
    fontFamily: 'InterRegular',
    fontSize: '14px',
    flex: '1 1 auto',
    flexShrink: 1,
    minWidth: state.selectProps.menuIsOpen ? 88 : 0,
    maxWidth: '100%',
    overflow: 'hidden',
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: 'var(--black-700)',
    fontFamily: 'InterRegular',
    fontSize: '14px',
    padding: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
  }),
  multiValueRemove: (base) => ({
    ...base,
    flexShrink: 0,
  }),
  ...extraStyles,
});

export const SUMMARIZED_MULTI_SELECT_CLASS = 'async-select-custom--summarized-multi';
export const SUMMARIZED_MULTI_MENU_OPEN_CLASS = 'async-select-custom--summarized-menu-open';

/** classNames helper for react-select control when using summarized multi-select. */
export const getSummarizedMultiSelectClassNames = (isMulti) => {
  if (!isMulti) return {};

  return {
    control: ({ menuIsOpen }) =>
      [SUMMARIZED_MULTI_SELECT_CLASS, menuIsOpen ? SUMMARIZED_MULTI_MENU_OPEN_CLASS : null].filter(Boolean).join(' '),
  };
};

/** Apply summarized styles only when isMulti is enabled. */
export const mergeSummarizedMultiSelectStyles = (isMulti, extraStyles = {}) =>
  isMulti ? getSummarizedMultiSelectStyles(extraStyles) : extraStyles;

/**
 * Merge custom select components with SummarizedMultiValue when isMulti is enabled.
 *
 * @param {boolean} isMulti
 * @param {Object} [baseComponents={}]
 * @param {number} [maxVisible=1]
 */
export const withSummarizedMultiValue = (isMulti, baseComponents = {}, maxVisible = 1) =>
  isMulti
    ? {
        ...baseComponents,
        ValueContainer: SummarizedValueContainer,
        MultiValue: createSummarizedMultiValue(maxVisible),
      }
    : baseComponents;
